import type { BookingConfirmation, PublicHold } from '@pa/api/public'
import { fmtTime, sundayOf, useToast } from '@pa/ui'
import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from 'react'
import { useRecaptcha, type BotInputs } from '@/features/bot'
import { ContactBlock, useClinicConfig, type LinkParams } from '@/features/clinic'
import {
  BlockedScreen,
  BookingPageNotFound,
  CapReachedScreen,
  ChooseScreen,
  CookiesBlockedScreen,
  EntryPanel,
  ExpiredScreen,
  IdentifyForm,
  LoadingScreen,
  LookupErrorScreen,
  NewPatientScreen,
  NotFoundScreen,
  PresetUnavailableScreen,
  UnavailableClinicScreen,
  UnavailableScreen,
  type IdentifyFields,
} from '@/features/entry'
import { confirmBooking } from '../hooks/confirmBooking'
import { useBookingHistory } from '../hooks/useBookingHistory'
import { createBrowserStepHistory, createMemoryStepHistory } from '../model/historyAdapter'
import { newIdempotencyKey, usePatientApi } from '../hooks/usePatientApi'
import { codeOf, useWeek, type WeekQuery } from '../hooks/useWeek'
import { initialMachine, reasonOf, reduce, type Flow, type Machine, type Outcome } from '../model/machine'
import { shownChoice } from '../model/resolve'
import { disappeared, type WeekSnapshot } from '../model/taken'
import { BookFailedScreen, BookUnconfirmedScreen } from './BookFailedScreen'
import { BookingLayout } from './BookingLayout'
import { EmbedOverlay, type EmbedHost } from './EmbedOverlay'
import { ConfirmingStep } from './ConfirmingStep'
import { DateTimeStep } from './DateTimeStep'
import { ReviewStep } from './ReviewStep'
import { TicketScreen } from './TicketScreen'
import { VisitStep } from './VisitStep'

/** "Step X of N": P-07, P-08, P-09 (N = 2 when P-07 is skipped for 1·1·1, UserFlows P-07). */
const stepsOf = (flow: Flow) => (flow.skipped ? 2 : 3)
const stepNo = (flow: Flow, step: 'P-07' | 'P-08' | 'P-09') =>
  (step === 'P-07' ? 1 : step === 'P-08' ? 2 : 3) - (flow.skipped ? 1 : 0)

/** The "Couldn't hold that time" codes: stay on P-08 with the time kept and a toast (Frontend §3.5). */
const HOLD_RETRYABLE = new Set(['internal_error', 'network_error', 'timeout'])
/** D-47 / API.md §4.8: a hold refused while a booking is in flight is retried once, about 2 s later. */
export const HOLD_IN_PROGRESS_RETRY_MS = 2000
export const BOOKING_IN_PROGRESS_TOAST = 'You already have a booking in progress. Please finish it first.'
/** Poll errors that leave the shown week in place (the next poll tries again); anything else leaves P-08. */
const POLL_TOLERATED = new Set(['network_error', 'timeout', 'internal_error', 'pf_unavailable', 'pf_error'])

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const weekKey = (q: WeekQuery) => `${q.locationId}|${q.visitTypeId}|${q.providerId}`

/** The embed keeps its steps on an internal stack: no browser history entries (Frontend §4.7, UserFlows §2.4). */
const memoryHistory = () => createMemoryStepHistory()

/**
 * The booking flow (Frontend §4.4, §4.5): on load, #1 config and #2 sessions in parallel; "existing" (or an
 * `existing=1` open) re-checks config; P-03 → #5 lookup → #6 options → P-07. Patient details stay in this
 * component's state only (never storage, URLs, logs or `postMessage`). On `pagehide` the #3 beacon ends the visit.
 *
 * `embed` (task 08): the `/c/{slug}/embed` iframe. The visit starts after the host handshake (or 5 s); entry screens
 * fill the panel (with its X), and from the patient's lookup on everything is in the overlay; the steps use an
 * internal stack (no history entries); closing ends the visit (#3 beacon) before the host removes the frame.
 */
export function BookingApp({ slug, link, embed }: { slug: string; link: LinkParams; embed?: EmbedHost }) {
  const [m, dispatch] = useReducer(reduce, undefined, () => initialMachine(link.existingOnly, embed !== undefined))
  const api = usePatientApi(slug)
  const config = useClinicConfig(slug).data
  const toast = useToast()
  // P-03's bot inputs (Frontend §4.6): they resolve once the score token is in (≤ 3 s).
  const bot = useRef<Promise<BotInputs> | null>(null)
  const recaptcha = useRecaptcha(config?.recaptcha)
  const state = m.state
  const timeZone = config?.clinic.timeZone ?? 'UTC'
  const phone = config?.contact.phone ?? ''
  const machine = useRef(m)
  useEffect(() => {
    machine.current = m
  })

  const releaseBack = useCallback(
    (holdId: string) => {
      api.releaseHold(holdId, 'back')
    },
    [api],
  )
  useBookingHistory(m, dispatch, releaseBack, embed ? memoryHistory : createBrowserStepHistory)

  // Session start (Frontend §4.4 step 1), again after P-16 "Start again" (bootId). The embed starts its visit after
  // the host handshake (or 5 s without one), with the embed-origin signal; config loads at once either way.
  useEffect(() => {
    let live = true
    const session = embed
      ? embed.start().then((embedOrigin) => api.startSession(link, { embedOrigin }))
      : api.startSession(link)
    void Promise.all([api.config(m.bootId > 0), session]).then(([c, started]) => {
      if (live) dispatch({ type: 'BOOTED', config: c, session: started })
    })
    return () => {
      live = false
    }
  }, [api, link, m.bootId, embed])

  // #4 events (API.md §4.4, task 18), fire-and-forget, on the P-01 choice only (never for an `existing=1` open, which
  // skips P-01): `new_chosen` when P-02 is shown, `existing_chosen` when P-03 or P-06 is shown after it.
  const shown = useRef(state.name)
  useEffect(() => {
    const from = shown.current
    shown.current = state.name
    if (from !== 'choose' || from === state.name) return
    if (state.name === 'newPatient') api.event('new_chosen')
    else if (state.name === 'identify' || state.name === 'unavailable') api.event('existing_chosen')
  }, [api, state.name])

  // "I'm an existing patient": re-check config (API.md §4.1; served from the server's 30 s cache).
  const checking = state.name === 'choose' && state.checking
  useEffect(() => {
    if (!checking) return
    let live = true
    void api.config(true).then((c) => {
      if (live) dispatch({ type: 'RECHECKED', config: c })
    })
    return () => {
      live = false
    }
  }, [api, checking])

  // P-03 Continue → #5 (names and DOB in the POST body only).
  const lookupFields = state.name === 'identify' && state.pending ? state.fields : null
  useEffect(() => {
    const inputs = bot.current
    if (!lookupFields || !inputs) return
    let live = true
    void inputs
      .then((b) => api.lookup(lookupFields, b))
      .then((r) => {
        if (!live) return
        if (!r.ok) dispatch({ type: 'LOOKUP_FAILED', code: r.code })
        else if (r.value.result === 'found') dispatch({ type: 'LOOKUP_FOUND', firstName: lookupFields.first.trim() })
        else dispatch({ type: 'LOOKUP_NOT_FOUND' })
      })
    return () => {
      live = false
    }
  }, [api, lookupFields])

  // Found → #6 (our database only, D-62).
  const loadingOptions = state.name === 'loadingOptions'
  useEffect(() => {
    if (!loadingOptions) return
    let live = true
    void api.options().then((r) => {
      if (!live) return
      dispatch(r.ok ? { type: 'OPTIONS_LOADED', options: r.value } : { type: 'OPTIONS_FAILED', code: r.code })
    })
    return () => {
      live = false
    }
  }, [api, loadingOptions])

  // P-08: #7 for the shown week (the window's first week until the patient moves).
  const flow = 'flow' in state ? state.flow : null
  const weekQuery =
    state.name === 'datetime' && flow?.choice.locationId && flow.choice.visitTypeId && flow.choice.providerId
      ? {
          locationId: flow.choice.locationId,
          visitTypeId: flow.choice.visitTypeId,
          providerId: flow.choice.providerId,
          weekStart: flow.dt.weekStart ?? sundayOf(flow.options.window.firstDate),
        }
      : null
  // Live updates (D-44): every 20 s while P-08 is shown with no hold (not while "Holding your time…").
  const polling = state.name === 'datetime' && !state.holding
  const week = useWeek(slug, weekQuery, flow?.dt.reload ?? 0, polling)
  const shownWeek = weekQuery && week.data?.weekStart === weekQuery.weekStart ? week.data : undefined
  // A failed poll keeps the shown week (the next poll tries again); a failed first load is the step's error state.
  const weekError = week.isError && !(shownWeek && POLL_TOLERATED.has(codeOf(week.error))) ? codeOf(week.error) : null
  useEffect(() => {
    if (weekError) dispatch({ type: 'WEEK_FAILED', code: weekError })
  }, [weekError])

  // A time of the picked day that was in the last answer and is missing now was taken by someone else: struck
  // through; if it was selected, the selection clears and a toast says so (UserFlows P-08, API.md §4.7).
  const lastWeek = useRef<WeekSnapshot | null>(null)
  const pickedDay = state.name === 'datetime' ? state.flow.dt.day : null
  const selected = state.name === 'datetime' && !state.holding ? (state.flow.dt.slot?.start ?? null) : null
  const viewing = state.name === 'datetime' && !state.holding
  const answerKey = weekQuery ? weekKey(weekQuery) : null
  useEffect(() => {
    if (!shownWeek || answerKey === null || week.isPlaceholderData) return
    const next = { key: answerKey, week: shownWeek }
    const gone = disappeared(lastWeek.current, next, pickedDay)
    lastWeek.current = next
    if (gone.length === 0 || !viewing) return
    if (selected !== null && gone.includes(selected)) {
      toast(`${fmtTime(selected, timeZone)} was just taken by another patient`, 'error')
    }
    dispatch({ type: 'SLOTS_GONE', starts: gone })
  }, [shownWeek, answerKey, week.isPlaceholderData, pickedDay, selected, viewing, toast, timeZone])

  // P-08 Continue → #8 ("Holding your time…"). The countdown is anchored to performance.now() (Frontend §4.9).
  // One request per Continue, even when React runs the effect twice (StrictMode in development).
  const holds = useRef(new WeakMap<object, Promise<Outcome<PublicHold>>>())
  const holding = state.name === 'datetime' && state.holding ? state : null
  useEffect(() => {
    const slot = holding?.flow.dt.slot
    if (!holding || !slot) return
    const { locationId, visitTypeId, providerId } = holding.flow.choice
    if (!locationId || !visitTypeId || !providerId) return
    // Keyed by this Continue's state object: a later Continue (after Change) places a new hold.
    let request = holds.current.get(holding)
    if (!request) {
      const body = { locationId, visitTypeId, providerId, start: slot.start }
      request = (async () => {
        const first = await api.placeHold(body)
        if (first.ok || first.code !== 'request_in_progress') return first
        // D-47: this patient's booking is in flight (another tab?): wait about 2 s and retry once.
        await wait(HOLD_IN_PROGRESS_RETRY_MS)
        return api.placeHold(body)
      })()
      holds.current.set(holding, request)
    }
    let live = true
    void request.then((r) => {
      if (!live) return
      if (r.ok) {
        dispatch({ type: 'HOLD_OK', hold: r.value, receivedAt: performance.now() })
        return
      }
      if (HOLD_RETRYABLE.has(r.code)) toast('Couldn’t hold that time. Try again.', 'error')
      if (r.code === 'request_in_progress') toast(BOOKING_IN_PROGRESS_TOAST, 'error')
      dispatch({ type: 'HOLD_FAILED', code: r.code })
    })
    return () => {
      live = false
    }
  }, [api, holding, toast])

  // P-09's bot inputs at Confirm (Frontend §4.6): the honeypot and the fill time from P-09's first render (a new hold's
  // P-09 mounts again, so an attempt after P-11 "Try again" starts again).
  const confirmBot = useRef<Promise<BotInputs>>(Promise.resolve({ scoreToken: null, hp: '', fillMs: 0 }))

  // P-10: #10 with this attempt's Idempotency-Key, sent once per key (Frontend §4.10). `confirmBooking` resends with
  // the same key on `request_in_progress` and replays a lost answer once; still unknown → the P-11 variant. The reason
  // travels in the POST body only (D-22). On 201 the progress lines finish, then P-12. The answer is applied even when
  // the patient left P-10 with browser Back (P-08): a booking that landed still shows P-12, an unknown one P-11.
  const bookings = useRef(new Map<string, Promise<Outcome<BookingConfirmation>>>())
  const [confirmed, setConfirmed] = useState<{
    key: string
    confirmation: BookingConfirmation
    reason: string | null
  } | null>(null)
  const attempt = state.name === 'confirming' ? state : null
  useEffect(() => {
    if (!attempt || bookings.current.has(attempt.key)) return
    const { key } = attempt
    const reason = reasonOf(attempt.flow.reason)
    // The score token first (≤ 3 s), then the attempt with its whole budget (Frontend §4.10).
    const request = confirmBot.current.then((inputs) =>
      confirmBooking((timeoutMs) => api.book(attempt.hold.id, reason, inputs, key, timeoutMs)),
    )
    bookings.current.set(key, request)
    void request.then((r) => {
      if (r.ok) {
        setConfirmed({ key, confirmation: r.value, reason })
        return
      }
      const now = machine.current.state
      if (now.name === 'confirming' && now.key === key) dispatch({ type: 'BOOK_FAILED', code: r.code })
      else if (r.code === 'booking_unconfirmed') dispatch({ type: 'LATE_UNCONFIRMED' })
    })
  }, [api, attempt])
  // A success that arrives after browser Back left P-10 (or during its closing animation) still ends on P-12.
  const leftConfirming = state.name !== 'confirming'
  useEffect(() => {
    if (confirmed && leftConfirming) {
      dispatch({ type: 'LATE_BOOKED', confirmation: confirmed.confirmation, reason: confirmed.reason })
    }
  }, [confirmed, leftConfirming])

  // `option_unavailable` at #7 / #8: options again (DB only), then P-07 with what still fits.
  const refetching = state.name === 'refetchingOptions'
  useEffect(() => {
    if (!refetching) return
    let live = true
    void api.options().then((r) => {
      if (!live) return
      dispatch(r.ok ? { type: 'OPTIONS_REFETCHED', options: r.value } : { type: 'OPTIONS_FAILED', code: r.code })
    })
    return () => {
      live = false
    }
  }, [api, refetching])

  // P-14 "Any hold is released" (UserFlows P-14, T-P14-01). A block of the visit already released it on the server; an
  // IP block or a rate limit answered before the service didn't, so the client releases it (#9 is idempotent and is
  // never refused for an IP block). P-15 (task 17) too: the server released it at the cap; the client drops it as well.
  const lastHold = useRef<string | null>(null)
  const heldId = state.name === 'review' || state.name === 'confirming' ? state.hold.id : null
  const dropsHold = state.name === 'blocked' || state.name === 'capReached'
  useEffect(() => {
    if (heldId !== null) lastHold.current = heldId
    else if (!dropsHold) lastHold.current = null
  }, [heldId, dropsHold])
  useEffect(() => {
    if (!dropsHold || lastHold.current === null) return
    api.releaseHold(lastHold.current)
    lastHold.current = null
  }, [api, dropsHold])

  // Leaving or closing the page ends the visit on the server (Frontend §4.4 step 6), once.
  const ended = useRef(false)
  const endVisit = useCallback(() => {
    if (ended.current) return
    ended.current = true
    api.endBeacon()
  }, [api])
  useEffect(() => {
    const onPageHide = () => {
      endVisit()
    }
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [endVisit])

  // Embed: the host's `close` ends the visit (the #3 beacon releases any hold), then `closed` lets the host remove
  // the frame, which forgets everything (UserFlows P-00). The frame's own X asks the host to close.
  useEffect(() => {
    if (!embed) return
    return embed.onHostClose(() => {
      endVisit()
      embed.closed()
    })
  }, [embed, endVisit])
  const closeFromFrame = useCallback(() => {
    endVisit()
    embed?.requestClose()
  }, [embed, endVisit])

  // Embed: `step { layout }` — the overlay from the patient's lookup on (P-06, P-14, P-16, P-17 after lookup too),
  // `booked` on P-12. UI events only.
  const overlayLayout = m.found
  const booked = state.name === 'booked'
  useEffect(() => {
    embed?.layout(overlayLayout ? 'overlay' : 'panel', booked)
  }, [embed, overlayLayout, booked])

  const clinicName = config?.clinic.name
  const contact = (compact: boolean) =>
    config ? <ContactBlock contact={config.contact} hours={config.hours} compact={compact} /> : null
  /** Embed after lookup: the booking overlay (logo, name, Close X) around the step. */
  const overlay = (children: ReactNode) =>
    embed ? (
      <EmbedOverlay clinicName={clinicName} logoUrl={config?.clinic.branding.logoUrl} onClose={closeFromFrame}>
        {children}
      </EmbedOverlay>
    ) : (
      children
    )
  const panel = (children: ReactNode) =>
    embed && !overlayLayout ? (
      <EntryPanel clinicName={clinicName} variant="embed" onClose={closeFromFrame} rootRef={embed.panelRef}>
        {children}
      </EntryPanel>
    ) : (
      overlay(<EntryPanel clinicName={clinicName}>{children}</EntryPanel>)
    )

  if (state.name === 'notFoundPage')
    return embed ? panel(<BookingPageNotFound variant="panel" />) : <BookingPageNotFound />
  const layout = (
    f: Flow,
    step: number,
    children: ReactNode,
    extra: { slot?: Flow['dt']['slot']; progress?: boolean } = {},
  ) =>
    overlay(
      <BookingLayout
        firstName={f.firstName}
        options={f.options}
        choice={shownChoice(f.options, f.choice)}
        phone={phone}
        step={step}
        steps={stepsOf(f)}
        slot={extra.slot ?? null}
        timeZone={timeZone}
        progress={extra.progress ?? true}
      >
        {children}
      </BookingLayout>,
    )
  /** P-11 and P-12: no summary panel (mockup `BookingFlow`, T-P11-01, T-P12-05). */
  const single = (children: ReactNode) =>
    overlay(
      <div className="pt-shell" style={{ gridTemplateColumns: '1fr' }}>
        <section className="pt-content">{children}</section>
      </div>,
    )

  switch (state.name) {
    case 'boot':
    case 'loadingOptions':
      return panel(<LoadingScreen />)
    case 'choose':
      return panel(
        <ChooseScreen
          clinicName={clinicName ?? 'this clinic'}
          busy={state.checking}
          onExisting={() => {
            dispatch({ type: 'CHOOSE_EXISTING' })
          }}
          onNew={() => {
            dispatch({ type: 'CHOOSE_NEW' })
          }}
        />,
      )
    case 'newPatient':
      return panel(
        <NewPatientScreen
          onBack={() => {
            dispatch({ type: 'BACK' })
          }}
          contact={contact(false)}
        />,
      )
    case 'identify':
      return panel(
        <IdentifyForm
          key={state.formKey}
          initial={state.fields}
          pending={state.pending}
          timeZone={config?.clinic.timeZone ?? 'UTC'}
          onBack={
            m.existingOnly
              ? undefined
              : () => {
                  dispatch({ type: 'BACK' })
                }
          }
          onSubmit={(fields: IdentifyFields, inputs: Promise<BotInputs>) => {
            bot.current = inputs
            dispatch({ type: 'LOOKUP_START', fields })
          }}
          recaptcha={recaptcha}
          challenge={state.challenge === true}
          onChallengeUnavailable={() => {
            dispatch({ type: 'CHALLENGE_UNAVAILABLE' })
          }}
        />,
      )
    case 'notFound':
      return panel(
        <NotFoundScreen
          onTryAgain={() => {
            dispatch({ type: 'TRY_AGAIN' })
          }}
          contact={contact(true)}
        />,
      )
    case 'lookupError':
      return panel(
        <LookupErrorScreen
          onTryAgain={() => {
            dispatch({ type: 'TRY_AGAIN' })
          }}
          contact={contact(true)}
        />,
      )
    case 'unavailable':
      return panel(
        <UnavailableScreen
          onBack={
            state.canGoBack
              ? () => {
                  dispatch({ type: 'BACK' })
                }
              : undefined
          }
          contact={state.contact ? contact(true) : null}
        />,
      )
    case 'expired':
      return panel(
        <ExpiredScreen
          onStartAgain={() => {
            dispatch({ type: 'START_AGAIN' })
          }}
        />,
      )
    case 'unavailableClinic':
      return panel(<UnavailableClinicScreen contact={contact(false)} />)
    case 'presetUnavailable':
      return panel(<PresetUnavailableScreen contact={contact(false)} />)
    case 'blocked':
      return panel(<BlockedScreen contact={contact(true)} />)
    case 'capReached':
      return panel(<CapReachedScreen contact={contact(false)} />)
    case 'cookiesBlocked':
      return panel(<CookiesBlockedScreen directLink={embed?.directLink ?? `/c/${slug}`} />)
    case 'visit':
      return layout(
        state.flow,
        stepNo(state.flow, 'P-07'),
        <VisitStep
          options={state.flow.options}
          choice={state.flow.choice}
          stepLabel={`Step 1 of ${String(stepsOf(state.flow))}`}
          onChoose={(patch) => {
            dispatch({ type: 'CHOOSE', patch })
          }}
          onNext={() => {
            dispatch({ type: 'CHOOSE_TIME' })
          }}
        />,
      )
    case 'refetchingOptions':
      return layout(state.flow, stepNo(state.flow, 'P-07'), <LoadingScreen />)
    case 'datetime': {
      const f = state.flow
      const n = stepNo(f, 'P-08')
      return layout(
        f,
        n,
        <DateTimeStep
          stepLabel={`Step ${String(n)} of ${String(stepsOf(f))}`}
          options={f.options}
          choice={f.choice}
          pick={f.dt}
          week={shownWeek}
          loading={shownWeek === undefined && weekError === null}
          holding={state.holding}
          banner={state.banner}
          timeZone={timeZone}
          phone={phone}
          onWeek={(weekStart) => {
            dispatch({ type: 'WEEK', weekStart })
          }}
          onPickDay={(day) => {
            dispatch({ type: 'PICK_DAY', day })
          }}
          onPickSlot={(slot) => {
            dispatch({ type: 'PICK_SLOT', slot })
          }}
          onBack={
            f.skipped
              ? undefined
              : () => {
                  dispatch({ type: 'BACK' })
                }
          }
          onContinue={() => {
            dispatch({ type: 'HOLD_START' })
          }}
        />,
        { slot: f.dt.slot },
      )
    }
    case 'review': {
      const f = state.flow
      const n = stepNo(f, 'P-09')
      const holdId = state.hold.id
      return layout(
        f,
        n,
        <ReviewStep
          key={holdId}
          stepLabel={`Step ${String(n)} of ${String(stepsOf(f))}`}
          options={f.options}
          choice={f.choice}
          hold={state.hold}
          reason={f.reason}
          reasonMaxLength={config?.reasonMaxLength ?? 500}
          timeZone={timeZone}
          onReason={(patch) => {
            dispatch({ type: 'REASON', patch })
          }}
          onBack={() => {
            api.releaseHold(holdId, 'back')
            dispatch({ type: 'CHANGE' })
          }}
          onExpired={() => {
            api.releaseHold(holdId, 'expired')
            dispatch({ type: 'HOLD_EXPIRED' })
          }}
          onConfirm={(inputs) => {
            confirmBot.current = inputs
            // A new Idempotency-Key per confirm attempt, the P-13 resubmit included (API.md §1.11, D-56).
            dispatch({ type: 'CONFIRM', key: newIdempotencyKey() })
          }}
          recaptcha={recaptcha}
          challenge={state.challenge === true}
          onChallengeUnavailable={() => {
            dispatch({ type: 'CHALLENGE_UNAVAILABLE' })
          }}
        />,
        { slot: state.hold },
      )
    }
    case 'confirming': {
      const done = confirmed?.key === state.key ? confirmed.confirmation : null
      return layout(
        state.flow,
        stepsOf(state.flow),
        <ConfirmingStep
          key={state.key}
          done={done !== null}
          onShown={() => {
            if (done) dispatch({ type: 'BOOKED', confirmation: done })
          }}
        />,
        { slot: state.hold, progress: false },
      )
    }
    case 'bookFailed':
      return single(
        <BookFailedScreen
          onTryAgain={() => {
            dispatch({ type: 'TRY_AGAIN' })
          }}
          contact={contact(true)}
        />,
      )
    case 'bookUnconfirmed':
      return single(<BookUnconfirmedScreen contact={contact(true)} />)
    case 'booked':
      return single(
        <TicketScreen
          confirmation={state.confirmation}
          reason={state.reason}
          firstName={state.flow.firstName}
          phone={phone}
          timeZone={timeZone}
        />,
      )
  }
}

export type { Machine }
